import * as React from 'react';
import { Link, useParams } from 'react-router';
import {
  CameraIcon,
  MapIcon,
  MapPinIcon,
  BoxIcon,
  ArrowRightIcon,
  EyeIcon,
  CheckCircle2Icon,
  TrophyIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PublicHeader } from '@/components/layout/public-header';
import { PublicFooter } from '@/components/layout/public-footer';
import { PublicHuntsList } from '@/components/hunt/public-hunts-list';
import { RegisteredFarmsList } from '@/components/farms/registered-farms-list';
import { appIdentity } from '@/app/app-config';
import { HOME_RENTAL_PROPERTIES } from '@/lib/home-rental-properties';
import { PURPOSES, getPurposeBySlug, type Purpose } from '@/lib/purposes';

/* ─── Hero ──────────────────────────────────────────────────────── */

function PurposeHero({ purpose }: { purpose: Purpose }) {
  const mapHref = purpose.features.find((feature) => feature.to.startsWith('/map'))?.to ?? '/map';

  return (
    <section className="relative isolate overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,var(--color-primary)/15,transparent)]"
      />
      <div
        aria-hidden
        className="absolute -left-40 -top-40 -z-10 h-[600px] w-[600px] rounded-full bg-primary/5 blur-3xl"
      />
      <div
        aria-hidden
        className="absolute -bottom-20 -right-32 -z-10 h-[400px] w-[400px] rounded-full bg-primary/5 blur-3xl"
      />

      <div className="mx-auto flex max-w-5xl flex-col items-center gap-6 px-4 pb-8 pt-16 text-center sm:pt-20 md:pb-12 md:pt-28">
        <div className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
          <purpose.icon className="h-3.5 w-3.5" aria-hidden />
          {purpose.heroBadge}
        </div>

        <h1 className="max-w-3xl text-4xl font-extrabold tracking-tight sm:text-5xl md:text-6xl">
          {purpose.heroHeadline}{' '}
          <span className="text-primary">{purpose.heroAccent}</span>
        </h1>

        <p className="max-w-xl text-lg leading-relaxed text-muted-foreground sm:text-xl">
          {purpose.heroSubhead}
        </p>

        <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
          <Button asChild size="lg" className="text-base">
            <Link to={purpose.primaryCtaTo}>
              <CameraIcon className="mr-2 h-5 w-5" aria-hidden />
              {purpose.primaryCtaLabel}
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="text-base">
            <Link to={mapHref}>
              <MapIcon className="mr-2 h-5 w-5" aria-hidden />
              Open Map
            </Link>
          </Button>
          <Button asChild size="lg" variant="outline" className="text-base">
            <Link to="/ar">
              <MapPinIcon className="mr-2 h-5 w-5" aria-hidden />
              Explore AR
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

/* ─── Scenario ──────────────────────────────────────────────────── */

function ScenarioSection({ purpose }: { purpose: Purpose }) {
  return (
    <section className="mx-auto max-w-5xl px-4 py-16 md:py-20">
      <div className="mb-10 text-center">
        <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
          {purpose.scenarioTitle}
        </h2>
        <p className="mx-auto mt-2 max-w-lg text-muted-foreground">
          {purpose.scenarioIntro}
        </p>
      </div>
      <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {purpose.scenarioSteps.map((step, idx) => (
          <div key={step.title} className="flex flex-col items-center text-center">
            <div className="relative mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/20">
              <CheckCircle2Icon className="h-6 w-6" aria-hidden />
              <span className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                {idx + 1}
              </span>
            </div>
            <h3 className="text-lg font-semibold">{step.title}</h3>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
              {step.description}
            </p>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─── Homes & rentals listings ──────────────────────────────────── */

function HomesRentalsListings() {
  return (
    <section className="bg-muted/30">
      <div className="mx-auto max-w-5xl px-4 py-16 md:py-20">
        <div className="mb-10 text-center">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">Homes & rentals</h2>
          <p className="mx-auto mt-2 max-w-lg text-muted-foreground">
            Properties currently available to select when reporting a maintenance issue.
          </p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {HOME_RENTAL_PROPERTIES.map((property) => (
            <Card key={property.id}>
              <CardContent className="flex flex-col gap-4 p-6">
                <div>
                  <h3 className="text-lg font-semibold">{property.name}</h3>
                  <address className="mt-1 text-sm not-italic leading-relaxed text-muted-foreground">
                    {property.address}
                  </address>
                </div>
                <div className="flex flex-wrap gap-2">
                  {property.twinPath && (
                    <Button asChild size="sm" className="w-fit">
                      <Link to={property.twinPath}>
                        <BoxIcon className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                        Explore 3D twin
                      </Link>
                    </Button>
                  )}
                  <Button asChild variant="outline" size="sm" className="w-fit">
                    <Link to={`/map?lat=${property.latitude}&lng=${property.longitude}&zoom=${property.zoom}&purpose=homes-rentals`}>
                      <MapPinIcon className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                      View on map
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Feature highlights ────────────────────────────────────────── */

function FeatureHighlights({ purpose }: { purpose: Purpose }) {
  return (
    <section className="bg-muted/30">
      <div className="mx-auto max-w-5xl px-4 py-16 md:py-20">
        <div className="mb-10 text-center">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Tools that fit your workflow
          </h2>
          <p className="mx-auto mt-2 max-w-lg text-muted-foreground">
            Everything you need to identify, report, and resolve issues on physical assets.
          </p>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {purpose.features.map((f) => (
            <Card key={f.title} className="group relative overflow-hidden transition-shadow hover:shadow-md">
              <CardContent className="flex flex-col gap-4 p-6">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/15 transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                  <f.icon className="h-5 w-5" aria-hidden />
                </div>
                <div className="flex flex-1 flex-col gap-1.5">
                  <h3 className="text-lg font-semibold">{f.title}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{f.description}</p>
                </div>
                <Button asChild variant="outline" size="sm" className="w-fit">
                  <Link to={f.to}>
                    {f.cta}
                    <ArrowRightIcon className="ml-1.5 h-3.5 w-3.5" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Organiser context (further down the page) ─────────────────── */

function OrganiserSection({ purpose }: { purpose: Purpose }) {
  return (
    <section className="mx-auto max-w-5xl px-4 py-16 md:py-20">
      <div className="rounded-2xl border border-border bg-muted/20 p-8 md:p-12">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-sm font-medium text-primary">
            <purpose.icon className="h-3.5 w-3.5" aria-hidden />
            {purpose.organiserBadge}
          </div>
          <h2 className="mt-4 text-2xl font-bold tracking-tight sm:text-3xl">
            {purpose.organiserHeading}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
            {purpose.organiserSubhead}
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─── Cross-links to other purposes ─────────────────────────────── */

function OtherPurposes({ currentSlug }: { currentSlug: string }) {
  const others = PURPOSES.filter((p) => p.slug !== currentSlug);
  if (others.length === 0) return null;

  return (
    <section className="mx-auto max-w-5xl px-4 py-16 md:py-20">
      <div className="mb-8 text-center">
        <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
          Also built for
        </h2>
        <p className="mx-auto mt-2 max-w-lg text-muted-foreground">
          Fix It Foundry adapts to any organisation managing physical assets.
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {others.map((p) => (
          <Link
            key={p.slug}
            to={`/for/${p.slug}`}
            className="group flex items-center gap-3 rounded-lg border border-border p-4 transition-colors hover:bg-accent"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/15">
              <p.icon className="h-5 w-5" aria-hidden />
            </div>
            <div className="flex flex-col">
              <span className="font-medium">{p.shortName}</span>
              <span className="text-sm text-muted-foreground">{p.tagline}</span>
            </div>
            <ArrowRightIcon className="ml-auto h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5" aria-hidden />
          </Link>
        ))}
      </div>
    </section>
  );
}

/* ─── Page wrapper ──────────────────────────────────────────────── */

function PurposePage({ purpose }: { purpose: Purpose }) {
  const isTreasureHunt = purpose.slug === 'treasure-hunt';

  React.useEffect(() => {
    document.title = `${appIdentity.name} — ${purpose.name}`;
  }, [purpose.name]);

  const scrollToAvailableHunts = () => {
    document.getElementById('available-hunts')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="flex min-h-dvh flex-col">
      <PublicHeader />
      <main className="flex-1">
        <PurposeHero purpose={purpose} />
        {isTreasureHunt ? <PublicHuntsList /> : null}
        {purpose.slug === 'farms' ? <RegisteredFarmsList /> : null}
        {purpose.slug === 'homes-rentals' ? <HomesRentalsListings /> : null}
        <ScenarioSection purpose={purpose} />
        <FeatureHighlights purpose={purpose} />
        <OrganiserSection purpose={purpose} />
        <OtherPurposes currentSlug={purpose.slug} />
      </main>
      <PublicFooter />

      {/* Sticky mobile CTA bar */}
      <div className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] backdrop-blur-md sm:hidden">
        {isTreasureHunt ? (
          <Button type="button" size="lg" className="w-full text-base" onClick={scrollToAvailableHunts}>
            <TrophyIcon className="mr-2 h-5 w-5" aria-hidden />
            Join Hunt
          </Button>
        ) : (
          <Button asChild size="lg" className="w-full text-base">
            <Link to={purpose.primaryCtaTo}>
              <CameraIcon className="mr-2 h-5 w-5" aria-hidden />
              {purpose.primaryCtaLabel}
            </Link>
          </Button>
        )}
      </div>
      <div className="h-[calc(72px+env(safe-area-inset-bottom))] sm:hidden" aria-hidden />
    </div>
  );
}

/** Route component — reads the :purpose param and renders the matching page. */
export function PurposeRoutePage() {
  const { purpose: slug } = useParams<{ purpose: string }>();
  const purpose = slug ? getPurposeBySlug(slug) : undefined;

  if (!purpose) {
    return (
      <div className="flex min-h-dvh flex-col">
        <PublicHeader />
        <main className="flex flex-1 flex-col items-center justify-center gap-4 px-4 py-20 text-center">
          <EyeIcon className="h-12 w-12 text-muted-foreground" aria-hidden />
          <h1 className="text-2xl font-bold tracking-tight">Purpose not found</h1>
          <p className="max-w-sm text-muted-foreground">
            We couldn&rsquo;t find that page. Explore what Fix It Foundry can do for your organisation.
          </p>
          <Button asChild>
            <Link to="/">Back to home</Link>
          </Button>
        </main>
        <PublicFooter />
      </div>
    );
  }

  return <PurposePage purpose={purpose} />;
}
